import React from 'react';
import { cn } from '../../../utils/cn';

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** The heading level */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Visual size variant (can be different from semantic level) */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
  /** Font weight */
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';
  /** Text color variant */
  color?: 'default' | 'muted' | 'accent' | 'primary';
  /** Whether to apply gradient text effect */
  gradient?: boolean;
}

const headingSizes = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
  '6xl': 'text-6xl',
};

const headingWeights = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold',
};

const headingColors = {
  default: 'text-neutral-900 dark:text-neutral-100',
  muted: 'text-neutral-600 dark:text-neutral-400',
  accent: 'text-accent-600 dark:text-accent-400',
  primary: 'text-primary-600 dark:text-primary-400',
};

const defaultSizeForLevel = {
  1: '4xl',
  2: '3xl',
  3: '2xl',
  4: 'xl',
  5: 'lg',
  6: 'md',
} as const;

const defaultWeightForLevel = {
  1: 'bold',
  2: 'bold',
  3: 'semibold',
  4: 'semibold',
  5: 'medium',
  6: 'medium',
} as const;

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    {
      className,
      level = 1,
      size,
      weight,
      color = 'default',
      gradient = false,
      children,
      ...props
    },
    ref
  ) => {
    const Component = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    const finalSize = size || defaultSizeForLevel[level];
    const finalWeight = weight || defaultWeightForLevel[level];

    return React.createElement(
      Component,
      {
        className: cn(
          // Base styles
          'tracking-tight',
          // Size
          headingSizes[finalSize],
          // Weight
          headingWeights[finalWeight],
          // Color (only if not gradient)
          !gradient && headingColors[color],
          // Gradient effect
          gradient && 'luxury-text-gradient',
          className
        ),
        ref,
        ...props
      },
      children
    );
  }
);

Heading.displayName = 'Heading';